// FormDesignModal.jsx

import React, { useEffect, useState } from 'react';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Services
import { subscribeToNode } from 'services/nodes/NodeReadService';

// CSS
import './FormDesignModal.css';

// Components
import Modal from 'components/modal/Modal';
import FormLayoutEditor from 'form/FormLayoutEditor';

/**
 * FormDesignModal
 *
 * Full-screen-ish modal hosting FormLayoutEditor for a form template.
 * When no preview node exists, uses block placeholders (Admin / empty bases).
 */
const FormDesignModal = ({
  fieldBlockTypeDefOverride = null,
  formDoc = null,
  getTypeOverride = null,
  isOpen = false,
  nodeKey = '',
  onClose,
  onPersistDocument = null,
  title = 'Design Form Template',
}) => {
  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();
  const cleanedNodeKey = String(nodeKey || '').trim();

  // ----- STATE ----- //

  const [previewNode, setPreviewNode] = useState(null);

  // ----- EFFECTS ----- //

  useEffect(() => {
    if (!isOpen || !cleanedNodeKey) {
      setPreviewNode(null);
      return undefined;
    }

    return subscribeToNode(cleanedNodeKey, setPreviewNode);
  }, [cleanedNodeKey, isOpen]);

  const canEditWithPlaceholders = Boolean(formDoc) && !cleanedNodeKey;
  const canEditWithPreview = Boolean(formDoc && cleanedNodeKey && previewNode);

  // ----- RENDER ----- //

  return (
    <Modal
      iconKey="design_services"
      isOpen={isOpen}
      title={title}
      width={isMobile ? '100%' : 1020}
      height={isMobile ? '100%' : '90%'}
      onClose={onClose}
    >

      {/* BODY */}
      <div className="form-design-modal-body">

        {/* EMPTY */}
        {!formDoc ? (
          <div className="form-design-modal-empty">
            Form template is not available.
          </div>
        ) : null}

        {cleanedNodeKey && !previewNode ? (
          <div className="form-design-modal-empty">
            Loading record preview…
          </div>
        ) : null}

        {(canEditWithPlaceholders || canEditWithPreview) ? (
          <FormLayoutEditor
            key={`${formDoc.key}-${formDoc.dateModified || 'draft'}`}
            fieldBlockTypeDefOverride={fieldBlockTypeDefOverride}
            formDoc={formDoc}
            getTypeOverride={getTypeOverride}
            previewNode={previewNode}
            useBlockPlaceholders={canEditWithPlaceholders}
            onPersistDocument={onPersistDocument}
          />
        ) : null}
      </div>
    </Modal>
  );
};

// Dev Tools
FormDesignModal.displayName = 'FormDesignModal';

export default FormDesignModal;
